// import logToSentry from '@shared/logToSentry';
import { validatePhoneForCountry } from '@shared/util/phoneNumber';
import { pwnedPassword } from 'hibp';
// eslint-disable-next-line no-restricted-imports
import * as Yup from 'yup';
// eslint-disable-next-line no-restricted-imports
import type { Flags } from 'yup';

const { addMethod, setLocale } = Yup;

// type augmentations only work with interfaces
/* eslint-disable @typescript-eslint/consistent-type-definitions */

setLocale({
  mixed: {
    // i18n-tasks-use t('error_messages.not_blank')
    required: () => ({ id: 'error_messages.not_blank' }),
  },
  number: {
    // i18n-tasks-use t('error_messages.greater_than_or_equal_to')
    min: ({ min }) => ({ id: 'error_messages.greater_than_or_equal_to', values: { min } }),
    // i18n-tasks-use t('error_messages.less_than_or_equal_to')
    max: ({ max }) => ({ id: 'error_messages.less_than_or_equal_to', values: { max } }),
    // i18n-tasks-use t('error_messages.must_be_an_integer')
    integer: () => ({ id: 'error_messages.must_be_an_integer' }),
  },
});

// String validators
declare module 'yup' {
  interface StringSchema<TType, TContext, TDefault, TFlags> {
    notBlank(
      errorMessage?: string,
    ): StringSchema<Exclude<TType, undefined>, TContext, TDefault, TFlags>;
    noEmoji(): this;
    latinChars(): this;
    requiredLatinFieldFor(fields: string[]): this;
    validPassword(): this;
    validPhone(): this;
    validEditorBody(): this;
    validUsername(): this;
    validEmail(errorMessage?: string): this;
    /** @deprecated use validEmail() */
    email(): never;
    confirmsPassword(confirms: string): this;
    maxChars(maxChars: number, errorMessage?: string): this;
    minChars(minChars: number, errorMessage?: string): this;
  }
}

const EMOJI_REGEX = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;

addMethod(Yup.string, 'noEmoji', function (this: Yup.StringSchema) {
  // i18n-tasks-use t('error_messages.no_emoji')
  return this.test('no-emoji', 'error_messages.no_emoji', (value) => {
    return !value || !EMOJI_REGEX.test(value);
  });
});

export const LATIN_CHARS_REGEX =
  // @babel/plugin-proposal-unicode-property-regex will transform this nicely
  /^[\p{Script_Extensions=Latin}\p{Script_Extensions=Common}]*$/u;

addMethod(Yup.string, 'latinChars', function (this: Yup.StringSchema) {
  return this.matches(LATIN_CHARS_REGEX, {
    excludeEmptyString: true,
    // i18n-tasks-use t('error_messages.only_latin_characters')
    message: 'error_messages.only_latin_characters',
  });
});

addMethod(Yup.string, 'requiredLatinFieldFor', function (this: Yup.StringSchema, fields: string[]) {
  return this.when(fields, (values: string[], schema) =>
    values.some((v) => !LATIN_CHARS_REGEX.test(v)) ? schema : schema.latinChars(),
  );
});

// i18n-tasks-use t('error_messages.not_blank')
addMethod(Yup.string, 'validEditorBody', function (this: Yup.StringSchema) {
  return this.test('hasNonEmptyContent', 'error_messages.not_blank', (value) => {
    if (!value) {
      return false;
    }
    return (
      value
        .trim()
        .replace(/<[^>]+>/g, '')
        .trim().length > 0
    );
  });
});

addMethod(Yup.string, 'validPassword', function (this: Yup.StringSchema) {
  // i18n-tasks-use t('error_messages.password_too_short')
  return this.test('len', 'error_messages.password_too_short', (val) => {
    // console.log('len', val, !val, val?.length)
    return !val || val.length >= 8;
    // i18n-tasks-use t('error_messages.password_is_pwned')
  }).test('password', 'error_messages.password_is_pwned', async (val?: string) => {
    // FIXME: Yup.test is called on *every key stroke*!
    // This happens even if we fiddle with the validateOnBlur/validateOnChange
    // settings in Formik to change when we show the messages.
    // This test however needs to call an external API so we need to
    // find a way to call the API only onSubmit (blocking the submit
    // and with the appropriate UI feedback) and for good UX
    // after suitable debounced interval on change.
    // Discussion at: https://github.com/jaredpalmer/formik/issues/512

    if (!val || val.length < 8) {
      return true; // only check passwords that are long enough
    }

    // console.log('pwnedPassword', val, !val, val?.length)
    return await pwnedPassword(val, { baseUrl: '/en/api/users/check_password' })
      .then((numPwns) => {
        // console.log(`"${val}" was found ${numPwns} time(s)`)
        return numPwns === 0;
      })
      /* istanbul ignore next */
      .catch(() => {
        // logToSentry(err as Error);
        // fail open if we cant reach service.. the server will also try
        return true;
      });
  });
});

// i18n-tasks-use t('error_messages.password_does_not_match_confirmation')
addMethod(Yup.string, 'confirmsPassword', function (this: Yup.StringSchema, confirms: string) {
  return this.oneOf([Yup.ref(confirms)], 'error_messages.password_does_not_match_confirmation');
});

const validPhoneLength = (val?: string) =>
  !val || val.replace(/[^0-9\u06F0-\u06F9\u0660-\u0669]/g, '').length >= 8;
const validInternationalPhone = (val?: string) =>
  !val || !!val.match(/^((\+|00)[\d \-()]+)|([\u06F0-\u06F9\u0660-\u0669+-]+)$/);

// the unicode ranges are arabic and persian numerals, from 0-9
addMethod(Yup.string, 'validPhone', function (this: Yup.StringSchema) {
  // i18n-tasks-use t('error_messages.phone_number_invalid')
  return this.test('phone', 'error_messages.phone_number_invalid', validPhoneLength)
    .test(
      'internaltional_phone',
      // i18n-tasks-use t('error_messages.phone_number_not_international')
      'error_messages.phone_number_not_international',
      validInternationalPhone,
    )
    .test(
      'country_phone',
      // i18n-tasks-use t('error_messages.phone_number_invalid_in_country')
      'error_messages.phone_number_invalid_in_country',
      validatePhoneForCountry,
    );
});

const strongEmailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

addMethod(Yup.string, 'validEmail', function (this: Yup.StringSchema, errorMessage?: string) {
  return this.test(
    'email',
    // i18n-tasks-use t('error_messages.email_invalid')
    errorMessage || 'error_messages.email_invalid',
    (val) => !val || strongEmailRegex.test(val),
  );
});

const emailValidatorInstance = Yup.string().validEmail();
// checks whether the input is either a valid email or phone number
// i18n-tasks-use t('error_messages.username_invalid')
addMethod(Yup.string, 'validUsername', function (this: Yup.StringSchema) {
  return this.test(
    'username',
    'error_messages.username_invalid',
    (val) =>
      !val ||
      (validPhoneLength(val) && validInternationalPhone(val)) ||
      emailValidatorInstance.isValidSync(val),
  );
});

// i18n-tasks-use t('error_messages.not_blank')
addMethod(Yup.string, 'notBlank', function (this: Yup.StringSchema, errorMessage?: string) {
  return this.trim().required(errorMessage || 'error_messages.not_blank');
});

addMethod(
  Yup.string,
  'maxChars',
  function (this: Yup.StringSchema, maxChars: number, errorMessage?: string) {
    return this.max(
      maxChars,
      // i18n-tasks-use t('error_messages.max_chars')
      errorMessage || { id: 'error_messages.max_chars', values: { maxChars } },
    );
  },
);

addMethod(
  Yup.string,
  'minChars',
  function (this: Yup.StringSchema, minChars: number, errorMessage?: string) {
    return this.min(
      minChars,
      // i18n-tasks-use t('error_messages.min_chars')
      errorMessage || { id: 'error_messages.min_chars', values: { minChars } },
    );
  },
);

// Date validators
declare module 'yup' {
  interface DateSchema {
    notBlank(): DateSchema;
    transformDate(): DateSchema;
    validDateRange(): DateSchema;
    limitFuture(maxDate: Date): DateSchema;
    minimumAge(years: number, errorMessage?: string): DateSchema;
    futureDate(): DateSchema;
  }
}

// eslint-disable-next-line @typescript-eslint/unbound-method
addMethod(Yup.date, 'notBlank', Yup.date.prototype.required);

addMethod(Yup.date, 'transformDate', function (this: Yup.DateSchema) {
  return this.transform((originalValue, currentValue) =>
    currentValue === '' ? null : originalValue,
  );
});
addMethod(Yup.date, 'validDateRange', function (this: Yup.DateSchema) {
  // i18n-tasks-use t('error_messages.date_too_old')
  return this.min(new Date(1920, 1, 1), 'error_messages.date_too_old').max(
    new Date(),
    // i18n-tasks-use t('error_messages.date_too_new')
    'error_messages.date_too_new',
  );
});

addMethod(Yup.date, 'limitFuture', function (this: Yup.DateSchema, maxDate: Date) {
  // i18n-tasks-use t('error_messages.date_in_future')
  return this.max(maxDate, { id: 'error_messages.date_in_future', values: { max_date: maxDate } });
});

addMethod(Yup.date, 'futureDate', function (this: Yup.DateSchema) {
  // i18n-tasks-use t('error_messages.date_in_future')
  const minDate = new Date();
  return this.min(minDate, { id: 'error_messages.date_must_be_in_future' });
});

addMethod(
  Yup.date,
  'minimumAge',
  function (this: Yup.DateSchema, years: number, errorMessage?: string) {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - years);
    return this.max(
      maxDate,
      errorMessage
        ? { id: errorMessage, values: { years } }
        : // i18n-tasks-use t('error_messages.minimum_age')
          { id: 'error_messages.minimum_age', values: { years } },
    );
  },
);

// Array validators
declare module 'yup' {
  interface ArraySchema<
    TIn extends any[] | null | undefined,
    TContext,
    TDefault = undefined,
    TFlags extends Flags = '',
  > {
    notBlank(errorMessage?: string): ArraySchema<TIn, TContext, TDefault, TFlags>;
    unique(options: {
      fieldPath: string;
      errorMessage?: string;
      ignoreCase?: boolean;
      name?: string;
    }): ArraySchema<TIn, TContext, TDefault, TFlags>;
  }
}

// i18n-tasks-use t('error_messages.not_blank')
addMethod(
  Yup.array,
  'notBlank',
  function (this: Yup.ArraySchema<any, any>, errorMessage = 'error_messages.not_blank') {
    return this.required(errorMessage).min(1, errorMessage);
  },
);

const getProp = (object: Object, keys: string | string[]) => {
  const [firstKey, ...otherKeys] = Array.isArray(keys) ? keys : keys.split('.');
  const recursedObject = object[firstKey as keyof typeof object];
  if (otherKeys.length > 0) {
    return getProp(recursedObject, otherKeys);
  }
  return recursedObject;
};

// i18n-tasks-use t('error_messages.email_not_unique')
addMethod(
  Yup.array,
  'unique',
  function (options?: {
    fieldPath?: string;
    errorMessage?: string;
    ignoreCase?: boolean;
    name?: string;
  }) {
    return this.test({
      name: 'duplicateFields',
      test: (cells) => {
        if (cells === undefined) {
          return true;
        }
        const fields = new Set<string>();
        for (let index = 0; index < cells.length; index++) {
          const item = cells[index];
          if (item === undefined) {
            continue;
          }

          const {
            name = 'data',
            ignoreCase = true,
            fieldPath = '',
            // i18n-tasks-use t('error_messages.email_not_unique')
            errorMessage = 'error_messages.email_not_unique',
          } = options || {};
          let comparableField = typeof item === 'object' ? getProp(item, fieldPath) : item;
          comparableField =
            ignoreCase && typeof comparableField === 'string'
              ? comparableField.toLowerCase()
              : comparableField;
          if (fields.has(comparableField)) {
            return new Yup.ValidationError(
              errorMessage,
              comparableField,
              `${name}.${index}${fieldPath ? `.${fieldPath}` : ''}`,
            );
          }
          fields.add(comparableField);
        }

        return fields.size === cells.length;
      },
    });
  },
);

// eslint-disable-next-line no-restricted-imports
export type { AnyObjectSchema, SchemaDescription } from 'yup';

export default Yup;
